/**
 * ShareReportsDialog
 *
 * Opened from the "Share" button on the Reports toolbar. Lets the user pick one
 * or more report suites (from the currently selected ClickHouse server) and
 * generates a shareable SSO URL that, after authentication, lands the recipient
 * on the chrome-less Direct Reports deep-link page:
 *
 *   <apiBaseUrl>/auth/sso/<providerId>/initiate?redirect_uri=
 *       <origin>/#/infralytiqs/direct-reports?server=<name>&reportsuites=<csv>&theme=<THEME>
 *
 * The deep link references suites BY NAME (the Direct Reports page resolves them
 * by name), so the link keeps working even if suite ids change.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, IconButton, InputAdornment, InputLabel, ListItemText,
  MenuItem, OutlinedInput, Select, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ContentCopy, OpenInNew, Close as CloseIcon, Link as LinkIcon, Save as SaveIcon,
  Visibility, VisibilityOff,
} from '@mui/icons-material';
import { RootState } from 'state/store';
import { SSOProviderPublic } from 'state/clientConfig/clientConfigSlice';
import { API_BASE_URL } from 'envConfig';
import { THEME_NAME_PROFESSIONAL } from 'theme';
import { preferenceToShareThemeType } from 'theme/themeMode';
import apiClient from 'utils/ApiClient';
import {
  COLOR_FIELD_GROUPS,
  ColorField,
  ShareSuiteOption,
  ShareTheme,
  ShareThemeType,
  buildCodeSnippet,
} from './shareReportSnippet';

interface ShareReportsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Name of the currently selected ClickHouse server (used as `server` param). */
  serverName: string;
  /** Report suites available on the selected server. */
  suites: ShareSuiteOption[];
  /** Suite id to preselect (the currently active suite), if any. */
  defaultSuiteId?: string;
}

/**
 * Special, non-SSO "Security Type" values appended to the SSO-provider list.
 * Prefixed with `__il_` so they can never collide with a real SSO provider id.
 */
const SECURITY_TYPE_IL_SDK = '__il_sdk__';
const SECURITY_TYPE_CREDENTIALS = '__il_credentials__';

export const ShareReportsDialog: React.FC<ShareReportsDialogProps> = ({
  open,
  onClose,
  serverName,
  suites,
  defaultSuiteId,
}) => {
  const apiBaseUrl = useSelector(
    (state: RootState) => state.clientConfig.data?.apiBaseUrl || API_BASE_URL,
  );
  const domainTheme = useSelector((state: RootState) => state.clientConfig.data?.theme);
  const globalMode = useSelector((state: RootState) => state.global.mode);
  const ssoProviders = useSelector(
    (state: RootState): SSOProviderPublic[] => state.clientConfig.data?.ssoProviders || [],
  );

  const defaultThemeParam: ShareTheme = domainTheme === THEME_NAME_PROFESSIONAL ? 'PROFESSIONAL' : 'DEFAULT';
  const defaultThemeType: ShareThemeType = preferenceToShareThemeType(globalMode);

  const [selectedSuiteIds, setSelectedSuiteIds] = useState<string[]>([]);
  const [defaultShareSuiteId, setDefaultShareSuiteId] = useState('');
  const [themeParam, setThemeParam] = useState<ShareTheme>(defaultThemeParam);
  const [themeType, setThemeType] = useState<ShareThemeType>(defaultThemeType);
  const [securityType, setSecurityType] = useState<string>('');
  const [colors, setColors] = useState<Record<string, string>>({});
  const [generatedUrl, setGeneratedUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [shareUsername, setShareUsername] = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [showSharePassword, setShowSharePassword] = useState(false);
  const [shareName, setShareName] = useState('');
  const [shareNameTouched, setShareNameTouched] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  // "Save" flow (Infralytiqs SDK snippets only): second-level popup asking for
  // a unique name, then persisted to IL-ReportSuites-Share via the API.
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reset selections each time the dialog is (re)opened.
  useEffect(() => {
    if (!open) return;
    setSelectedSuiteIds(defaultSuiteId ? [defaultSuiteId] : []);
    setDefaultShareSuiteId(defaultSuiteId || '');
    setThemeParam(defaultThemeParam);
    setThemeType(defaultThemeType);
    setSecurityType(ssoProviders[0]?.id || SECURITY_TYPE_IL_SDK);
    setColors({});
    setGeneratedUrl('');
    setCopied(false);
    setShareUsername('');
    setSharePassword('');
    setShowSharePassword(false);
    setShareName('');
    setShareNameTouched(false);
    setGenerateError('');
  }, [open, defaultSuiteId, defaultThemeParam, defaultThemeType, ssoProviders]);

  const setColor = (key: string, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    setGeneratedUrl('');
  };

  const isIlSdk = securityType === SECURITY_TYPE_IL_SDK;
  const isCredentials = securityType === SECURITY_TYPE_CREDENTIALS;
  const isSsoProvider = !isIlSdk && !isCredentials && securityType.length > 0;

  const selectedSuiteNames = useMemo(
    () => suites.filter((s) => selectedSuiteIds.includes(s._id)).map((s) => s.name),
    [suites, selectedSuiteIds],
  );

  const selectedSuites = useMemo(
    () => suites.filter((s) => selectedSuiteIds.includes(s._id)),
    [suites, selectedSuiteIds],
  );

  const defaultShareSuiteName = useMemo(
    () => selectedSuites.find((s) => s._id === defaultShareSuiteId)?.name || selectedSuiteNames[0] || '',
    [selectedSuites, defaultShareSuiteId, selectedSuiteNames],
  );

  const applySuiteSelection = (ids: string[]) => {
    setSelectedSuiteIds(ids);
    setDefaultShareSuiteId((prev) => (ids.includes(prev) ? prev : (ids[0] || '')));
    setGeneratedUrl('');
  };

  const suggestedShareName = useMemo(() => {
    const suitesPart = selectedSuiteNames.join(', ') || 'Report suite';
    const userPart = shareUsername.trim() || 'credentials';
    return `${suitesPart} · ${userPart}`;
  }, [selectedSuiteNames, shareUsername]);

  useEffect(() => {
    if (!open || !isCredentials || shareNameTouched) return;
    setShareName(suggestedShareName);
  }, [open, isCredentials, shareNameTouched, suggestedShareName]);

  const buildDeepLink = (): string => {
    const origin = window.location.origin;
    const params = new URLSearchParams();
    params.set('server', serverName);
    params.set('reportsuites', selectedSuiteNames.join(','));
    if (defaultShareSuiteName) params.set('defaultsuite', defaultShareSuiteName);
    params.set('theme', themeParam);
    params.set('themetype', themeType);
    // Carry the SSO provider in the deep link so the Direct Reports page can
    // re-trigger SSO (via the original shared link) when the session expires.
    if (isSsoProvider) params.set('sso', securityType);
    return `${origin}/#/infralytiqs/direct-reports?${params.toString()}`;
  };

  /**
   * Builds the embeddable code snippet for the "Infralytiqs SDK" security type.
   */
  const buildCodeSnippetLocal = (): string => buildCodeSnippet(
    window.location.origin,
    apiBaseUrl,
    serverName,
    selectedSuiteNames,
    themeParam,
    themeType,
    colors,
  );

  const persistCredentialsShare = async (): Promise<string> => {
    const res = await apiClient.post<{ snippet?: string; _id?: string }>(
      '/il/share/report-suites',
      {
        securityType: 'Credentials',
        name: shareName.trim(),
        username: shareUsername.trim(),
        password: sharePassword,
        reportSuites: selectedSuiteNames,
        defaultReportSuite: defaultShareSuiteName,
        server: serverName,
        theme: themeParam,
        themeType,
        origin: window.location.origin,
      },
    );
    const snippet = res?.data?.snippet;
    const shareId = res?.data?._id;
    if (snippet) return snippet;
    if (shareId) return `${window.location.origin}/#/infralytiqs/shared-reports/${shareId}`;
    throw new Error('Share was saved but no link was returned');
  };

  const handleGenerate = async () => {
    setGenerateError('');
    if (isIlSdk) {
      setGeneratedUrl(buildCodeSnippetLocal());
      setCopied(false);
      return;
    }
    if (isCredentials) {
      setGenerating(true);
      try {
        const url = await persistCredentialsShare();
        setGeneratedUrl(url);
        setCopied(false);
        setSaveSuccess(true);
      } catch (error: any) {
        setGenerateError(error?.message || 'Error generating shareable link');
      } finally {
        setGenerating(false);
      }
      return;
    }
    const deepLink = buildDeepLink();
    const base = (apiBaseUrl || '').replace(/\/+$/, '');
    const url = isSsoProvider
      ? `${base}/auth/sso/${encodeURIComponent(securityType)}/initiate?redirect_uri=${encodeURIComponent(deepLink)}`
      : deepLink;
    setGeneratedUrl(url);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleOpenSaveDialog = () => {
    setSaveName('');
    setSaveError('');
    setSaveDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    const name = saveName.trim();
    if (!name || !generatedUrl) return;
    setSaving(true);
    setSaveError('');
    try {
      await apiClient.post('/il/share/report-suites', {
        name,
        snippet: generatedUrl,
        reportSuites: selectedSuiteNames,
        defaultReportSuite: defaultShareSuiteName,
        server: serverName,
      });
      setSaveDialogOpen(false);
      setSaveSuccess(true);
    } catch (error: any) {
      setSaveError(error?.message || 'Error saving code snippet');
    } finally {
      setSaving(false);
    }
  };

  const canGenerate = serverName.trim().length > 0
    && selectedSuiteIds.length > 0
    && (!isCredentials || (shareName.trim().length > 0 && shareUsername.trim().length > 0 && sharePassword.length >= 6))
    && !generating;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={isIlSdk ? 'md' : 'sm'} PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon fontSize="small" />
          <Typography variant="h6" component="span" fontWeight={600}>Share report suites</Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Generate a link that opens the selected report suite(s) from
          {' '}
          <strong>{serverName || 'the selected server'}</strong>
          {' '}
          on a standalone page after sign-in.
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel>Report Suites</InputLabel>
          <Select
            multiple
            label="Report Suites"
            value={selectedSuiteIds}
            onChange={(e) => {
              const v = e.target.value;
              applySuiteSelection(typeof v === 'string' ? v.split(',') : v);
            }}
            input={<OutlinedInput label="Report Suites" />}
            renderValue={(selected) => suites
              .filter((s) => (selected as string[]).includes(s._id))
              .map((s) => s.name)
              .join(', ')}
          >
            {suites.length === 0 && (
              <MenuItem value="" disabled>No report suites available</MenuItem>
            )}
            {suites.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                <Checkbox checked={selectedSuiteIds.includes(s._id)} />
                <ListItemText primary={s.name} secondary={s.description} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedSuiteIds.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel>Default Report Suite</InputLabel>
            <Select
              label="Default Report Suite"
              value={selectedSuiteIds.includes(defaultShareSuiteId) ? defaultShareSuiteId : (selectedSuiteIds[0] || '')}
              onChange={(e) => { setDefaultShareSuiteId(e.target.value as string); setGeneratedUrl(''); }}
            >
              {selectedSuites.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mx: 1.75 }}>
              Loaded first on the shareable link. Recipients can still switch to the other selected suites.
            </Typography>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
            <InputLabel>Theme</InputLabel>
            <Select
              label="Theme"
              value={themeParam}
              onChange={(e) => { setThemeParam(e.target.value as ShareTheme); setGeneratedUrl(''); }}
            >
              <MenuItem value="DEFAULT">Default</MenuItem>
              <MenuItem value="PROFESSIONAL">Professional</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
            <InputLabel>Theme Type</InputLabel>
            <Select
              label="Theme Type"
              value={themeType}
              onChange={(e) => { setThemeType(e.target.value as ShareThemeType); setGeneratedUrl(''); }}
            >
              <MenuItem value="LIGHT">Light</MenuItem>
              <MenuItem value="DARK">Dark</MenuItem>
              <MenuItem value="AUTOMATIC">Automatic</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Security Type</InputLabel>
            <Select
              label="Security Type"
              value={securityType}
              onChange={(e) => { setSecurityType(e.target.value as string); setGeneratedUrl(''); }}
            >
              {ssoProviders.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.buttonName || p.id}</MenuItem>
              ))}
              <MenuItem value={SECURITY_TYPE_IL_SDK}>Infralytiqs SDK</MenuItem>
              <MenuItem value={SECURITY_TYPE_CREDENTIALS}>Credentials</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isIlSdk && (
          <Alert severity="info">
            Generates a code snippet. Paste it on any page that loads the Infralytiqs SDK; the selected
            report suite(s) render in-place, authenticated with the token the SDK stores in session storage.
            {themeType === 'AUTOMATIC' && (
              <>
                {' '}
                Theme Type <strong>Automatic</strong> follows the host page light/dark theme
                (for example Tenant Admin header) and updates at runtime without reloading the page.
              </>
            )}
          </Alert>
        )}

        {isIlSdk && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Color overrides (optional)
              </Typography>
            </Divider>
            <Typography variant="caption" color="text.secondary">
              Leave a field blank to keep the selected theme&apos;s default. Values can be any HEX color
              (e.g. <code>#1473E6</code>); they are forced onto the embedded report suite.
            </Typography>
            {COLOR_FIELD_GROUPS.map((grp) => (
              <Box key={grp.group} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {grp.group}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {grp.fields.map((f) => (
                    <ColorField
                      key={f.key}
                      label={f.label}
                      value={colors[f.key] || ''}
                      onChange={(v) => setColor(f.key, v)}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {isCredentials && (
          <Alert severity="info">
            Recipients open the generated link, sign in with the shareable username and password,
            then see the selected report suite(s) with a lightweight branded header.
            The link and credentials are saved under Saved Reports.
          </Alert>
        )}

        {isCredentials && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2">Shareable credentials</Typography>
            <TextField
              size="small"
              fullWidth
              label="Name"
              helperText="Saved Reports name. Pre-filled from the suite and username — you can change it."
              value={shareName}
              onChange={(e) => {
                setShareNameTouched(true);
                setShareName(e.target.value);
                setGeneratedUrl('');
                setGenerateError('');
              }}
              autoComplete="off"
              disabled={generating}
            />
            <TextField
              size="small"
              fullWidth
              label="Username"
              value={shareUsername}
              onChange={(e) => { setShareUsername(e.target.value); setGeneratedUrl(''); setGenerateError(''); }}
              autoComplete="off"
              disabled={generating}
            />
            <TextField
              size="small"
              fullWidth
              type={showSharePassword ? 'text' : 'password'}
              label="Password"
              helperText="At least 6 characters. Stored hashed — it cannot be retrieved later."
              value={sharePassword}
              onChange={(e) => { setSharePassword(e.target.value); setGeneratedUrl(''); setGenerateError(''); }}
              autoComplete="new-password"
              disabled={generating}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={showSharePassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowSharePassword((v) => !v)}
                      edge="end"
                    >
                      {showSharePassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {generateError && <Alert severity="error">{generateError}</Alert>}

        <Button
          variant="contained"
          color="secondary"
          onClick={() => void handleGenerate()}
          disabled={!canGenerate}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generating ? 'Generating…' : (isIlSdk ? 'Generate code snippet' : 'Generate link')}
        </Button>

        {generatedUrl && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label={isIlSdk ? 'Code snippet' : 'Shareable URL'}
              value={generatedUrl}
              size="small"
              fullWidth
              multiline
              minRows={isIlSdk ? 6 : 2}
              maxRows={isIlSdk ? 16 : 6}
              InputProps={{ readOnly: true, sx: isIlSdk ? { fontFamily: 'monospace', fontSize: 12 } : undefined }}
              onFocus={(e) => e.target.select()}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<ContentCopy />} onClick={handleCopy} variant="outlined" color="secondary">
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              {isIlSdk && (
                <Button size="small" startIcon={<SaveIcon />} onClick={handleOpenSaveDialog} variant="outlined" color="secondary">
                  Save
                </Button>
              )}
              {!isIlSdk && (
                <Button
                  size="small"
                  startIcon={<OpenInNew />}
                  variant="outlined"
                  color="secondary"
                  onClick={() => window.open(generatedUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Second-level popup: name + confirm to persist the generated snippet. */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => !saving && setSaveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon fontSize="small" />
            <Typography variant="h6" component="span" fontWeight={600}>Save code snippet</Typography>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton size="small" onClick={() => setSaveDialogOpen(false)} disabled={saving} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Give this share a name. The name must be unique within your company.
          </Typography>
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="Name"
            value={saveName}
            onChange={(e) => { setSaveName(e.target.value); setSaveError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && saveName.trim() && !saving) {
                e.preventDefault();
                void handleConfirmSave();
              }
            }}
            disabled={saving}
          />
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="secondary" onClick={() => setSaveDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => void handleConfirmSave()}
            disabled={!saveName.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          {isCredentials ? 'Shareable link and credentials saved' : 'Code snippet saved successfully'}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ShareReportsDialog;
